import { useEffect } from 'react';

// Adds drag-to-resize column widths and click-to-sort headers to every table
// rendered inside a modal (`Modal` body or a CSS-class `.modal`), without
// touching each call site. Works on the DOM because those tables are plain
// <table> markup owned by many different pages.
// Opt out per table with `data-no-enhance`.
//
// Page tables (inside `.content-card`) get resize only, and the widths are
// remembered per page in localStorage. No DOM sorting there: those tables are
// paginated/filtered by React, and reordering its rows behind its back would
// corrupt the next render. Double-click a resize handle to reset the table.
// Tables already using <ResizableTh> are skipped.
const MODAL_SELECTOR = '.app-modal-body table, .modal table';
const PAGE_SELECTOR = '.content-card table';
const MIN_WIDTH = 50;
const STORAGE_PREFIX = 'pc_auto_col_widths_';

const isModalTable = (table) => !!table.closest('.app-modal-body, .modal');

// Sort arrows (▲/▼) come and go with page state, so they must not be part of the saved-width key.
const headerKey = (th, idx) => (th.textContent || '').replace(/[▲▼]/g, '').trim() || `col${idx}`;

const readSaved = (storageId) => {
  try { return JSON.parse(localStorage.getItem(storageId) || '{}'); } catch { return {}; }
};
const writeSaved = (storageId, value) => {
  try {
    if (value === null) localStorage.removeItem(storageId);
    else localStorage.setItem(storageId, JSON.stringify(value));
  } catch { /* storage unavailable — widths just won't persist */ }
};

const cellText = (tr, idx) => (tr.children[idx]?.textContent || '').trim();

const compareValues = (a, b) => {
  const na = parseFloat(a.replace(/,/g, ''));
  const nb = parseFloat(b.replace(/,/g, ''));
  if (a !== '' && b !== '' && !isNaN(na) && !isNaN(nb) && /^-?[\d.,]+/.test(a) && /^-?[\d.,]+/.test(b)) return na - nb;
  return a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' });
};

const enhanceTable = (table) => {
  if (table.dataset.enhanced || table.hasAttribute('data-no-enhance')) return;
  const headRow = table.tHead?.rows?.[0];
  const body = table.tBodies?.[0];
  if (!headRow || !body || headRow.cells.length < 2) return;
  const modal = isModalTable(table);
  const ths = Array.from(headRow.cells);
  if (!modal) {
    // Page tables: need real columns, an existing <ResizableTh> means it is handled.
    if (ths.length < 3 || headRow.querySelector('.col-resize-handle')) return;
    // Hidden (e.g. inactive tab) tables have no measurable widths yet — retry on next mutation.
    if (table.offsetWidth === 0) return;
  }
  table.dataset.enhanced = '1';

  const storageId = modal ? null
    : `${STORAGE_PREFIX}${window.location.pathname}|${ths.map(headerKey).join('|')}`;
  const originalTableStyle = { layout: table.style.tableLayout, width: table.style.width };
  const originalThWidths = ths.map((th) => th.style.width);

  let sortIdx = -1;
  let sortDir = 1;

  const applyIndicators = () => {
    ths.forEach((th, i) => {
      const ind = th.querySelector('.mte-sort-ind');
      if (ind) ind.textContent = i === sortIdx ? (sortDir === 1 ? ' ▲' : ' ▼') : '';
    });
  };

  const sortBy = (idx) => {
    sortDir = sortIdx === idx ? -sortDir : 1;
    sortIdx = idx;
    const rows = Array.from(body.rows).filter((r) => r.cells.length === ths.length);
    if (rows.length < 2) return;
    const sorted = rows
      .map((r, i) => ({ r, i, v: cellText(r, idx) }))
      .sort((x, y) => (compareValues(x.v, y.v) * sortDir) || (x.i - y.i))
      .map((x) => x.r);
    sorted.forEach((r) => body.appendChild(r));
    applyIndicators();
  };

  const updateTableWidth = () => {
    table.style.width = `${ths.reduce((s, th) => s + (parseFloat(th.style.width) || th.getBoundingClientRect().width), 0)}px`;
  };

  // Freeze current widths so resizing one column doesn't reflow the rest.
  let frozen = false;
  const freeze = () => {
    if (frozen) return;
    const measured = ths.map((th) => th.getBoundingClientRect().width);
    ths.forEach((th, i) => { th.style.width = `${measured[i]}px`; });
    table.style.tableLayout = 'fixed';
    table.style.width = `${measured.reduce((s, w) => s + w, 0)}px`;
    if (!modal) table.classList.add('col-resized');
    frozen = true;
  };

  const persist = () => {
    if (!storageId) return;
    const saved = {};
    ths.forEach((th, i) => { saved[headerKey(th, i)] = Math.round(parseFloat(th.style.width) || 0); });
    writeSaved(storageId, saved);
  };

  const reset = () => {
    ths.forEach((th, i) => { th.style.width = originalThWidths[i]; });
    table.style.tableLayout = originalTableStyle.layout;
    table.style.width = originalTableStyle.width;
    table.classList.remove('col-resized');
    frozen = false;
    if (storageId) writeSaved(storageId, null);
  };

  ths.forEach((th, idx) => {
    th.style.position = 'relative';
    if (modal) {
      th.style.cursor = 'pointer';
      th.title = th.title || 'Click to sort';
      const ind = document.createElement('span');
      ind.className = 'mte-sort-ind';
      th.appendChild(ind);
      th.addEventListener('click', () => sortBy(idx));
    }

    const handle = document.createElement('span');
    handle.className = 'col-resize-handle';
    if (!modal) handle.title = 'Drag to resize · double-click to reset';
    handle.addEventListener('click', (e) => e.stopPropagation());
    handle.addEventListener('dblclick', (e) => {
      e.stopPropagation();
      if (!modal) reset();
    });
    handle.addEventListener('mousedown', (e) => {
      e.preventDefault();
      e.stopPropagation();
      freeze();
      const startX = e.clientX;
      const startW = th.getBoundingClientRect().width;
      const onMove = (ev) => {
        th.style.width = `${Math.max(MIN_WIDTH, startW + ev.clientX - startX)}px`;
        updateTableWidth();
      };
      const onUp = () => {
        document.removeEventListener('mousemove', onMove);
        document.removeEventListener('mouseup', onUp);
        persist();
      };
      document.addEventListener('mousemove', onMove);
      document.addEventListener('mouseup', onUp);
    });
    th.appendChild(handle);
  });

  // Restore this page's remembered layout (unsaved columns keep their measured width).
  if (storageId) {
    const saved = readSaved(storageId);
    if (Object.keys(saved).length) {
      freeze();
      ths.forEach((th, i) => {
        const w = saved[headerKey(th, i)];
        if (w >= MIN_WIDTH) th.style.width = `${w}px`;
      });
      updateTableWidth();
    }
  }
};

export default function ModalTableEnhancer() {
  useEffect(() => {
    let scheduled = false;
    const run = () => {
      scheduled = false;
      document.querySelectorAll(`${MODAL_SELECTOR}, ${PAGE_SELECTOR}`).forEach(enhanceTable);
    };
    const observer = new MutationObserver(() => {
      if (scheduled) return;
      scheduled = true;
      requestAnimationFrame(run);
    });
    observer.observe(document.body, { childList: true, subtree: true });
    run();
    return () => observer.disconnect();
  }, []);
  return null;
}
